import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { currentPeriodSelector } from '../../shared/store/selectors';
import { fetchReportParams } from './api';
import { EntityOption } from './types';
import { parseJsonStringOrFallback } from '@common/utils/jsonUtils';
import type { BatchReportDefaultParams } from '@common/types/batchReports';
import { BatchReportDetails } from '@client-shared/types/batchReports';

const parseCompanyCode = (defaultParams: string): EntityOption | null => {
  const parsed = parseJsonStringOrFallback<BatchReportDefaultParams>(defaultParams, {});
  const cCode = parsed.companyCode as Partial<EntityOption> | undefined;
  return cCode && cCode.value && cCode.label
    ? { value: String(cCode.value), label: String(cCode.label) }
    : null;
};

export const useCompanyCodeOptions = (batchReportDetails: BatchReportDetails | null) => {
  const currentPeriod = useSelector(currentPeriodSelector);
  const [companyCode, setCompanyCode] = useState<EntityOption | null>(null);
  const [isLoadingCompanyCodes, setIsLoadingCompanyCodes] = useState(false);
  const [companyCodeOptions, setCompanyCodeOptions] = useState<EntityOption[]>([]);

  const defaultParams = batchReportDetails?.defaultParams || '';

  const getCompanyCodeOptions = useCallback(async () => {
    setIsLoadingCompanyCodes(true);
    const params = await fetchReportParams(currentPeriod.name);
    setCompanyCodeOptions(params || []);
    setIsLoadingCompanyCodes(false);
  }, [currentPeriod.name]);

  useEffect(() => {
    const cCode = parseCompanyCode(defaultParams);
    if (cCode) {
      setCompanyCode(cCode);
    }
  }, [defaultParams]);

  useEffect(() => {
    if (companyCodeOptions.length === 0) {
      getCompanyCodeOptions();
    }
  }, [companyCodeOptions, getCompanyCodeOptions]);

  return {
    companyCode,
    setCompanyCode,
    companyCodeOptions,
    isLoadingCompanyCodeOptions: isLoadingCompanyCodes,
  };
};
